/**
 * Belote — server-authoritative rules engine (classic French partnership trick game, 4 players).
 *
 * Rules implemented (toggles live in {@link BeloteConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). 32-card deck (7 to ace).
 *  - Each hand: five cards to every player, then the next card is turned face up. Starting left of the dealer, each
 *    player may take that card's suit as trump (first round) or pass. If all four pass, a second round lets a player
 *    name any other suit as trump. If all pass again the hand is thrown in and the next dealer redeals.
 *  - The taker receives the turned-up card; the remaining cards are dealt so everyone holds eight. The player left
 *    of the dealer leads the first of eight tricks.
 *  - Card order and values — trump: J (20), 9 (14), A (11), 10 (10), K (4), Q (3), 8, 7. Other suits: A (11), 10 (10),
 *    K (4), Q (3), J (2), 9, 8, 7. The last trick is worth 10 extra ("dix de der"), so a hand holds 162 points.
 *  - Play: follow the led suit; a void player must trump unless the partner is currently winning the trick, and must
 *    overtrump when able. When trump is led, play a higher trump if able. Highest trump, else highest led card, wins.
 *  - Belote-rebelote: the holder of the king and queen of trumps scores 20 for the partnership (announced when the
 *    second of the two is played).
 *  - Scoring: the taking team must score more than the defenders (cards, dix de der and belote included). If it does
 *    not, it scores nothing and the defenders take all 162 points. A team that wins all eight tricks (capot) adds 90.
 *    The belote bonus always stays with its holders.
 *  - The match ends when a partnership reaches `targetScore` and leads; a tie plays another hand. `maxHands` is a
 *    hard cap so a match always terminates. Partners share a place, so payouts are split between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface BeloteConfig {
  /** Match ends when a partnership reaches this score and leads. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
  trickEndDelayMs: number;
}

export type BelotePhase = 'BID1' | 'BID2' | 'PLAYING' | 'TRICK_END' | 'HAND_END' | 'MATCH_END';

export interface BeloteTrickPlay {
  seat: number;
  card: CardId;
}

export interface BeloteCompletedTrick {
  leader: number;
  plays: BeloteTrickPlay[];
  winner: number;
}

/** One bidding call, `suit: null` = pass. */
export interface BeloteBid {
  seat: number;
  round: 1 | 2;
  suit: Suit | null;
}

/** Scoring breakdown of a finished hand (public). */
export interface BeloteHandResult {
  taker: number;
  trump: Suit;
  /** Card points plus dix de der per team, before belote/capot. */
  cardPoints: number[];
  /** Team that held the king and queen of trumps, or null. */
  beloteTeam: number | null;
  /** Team that won all eight tricks, or null. */
  capotTeam: number | null;
  /** Whether the taking team kept its contract. */
  made: boolean;
  /** Points added to each team's match score. */
  points: number[];
}

export interface BeloteState {
  config: BeloteConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: BelotePhase;
  hands: CardId[][];
  /** Card turned up for bidding. */
  upCard: CardId;
  /** Undealt cards (hidden from everyone) until a taker is found. */
  kitty: CardId[];
  bids: BeloteBid[];
  trump: Suit | null;
  taker: number | null;
  /** Seat that holds both the king and queen of trumps (hidden until announced). */
  beloteSeat: number | null;
  beloteAnnounced: boolean;
  tricksWon: number[];
  turn: number;
  trick: BeloteTrickPlay[];
  tricks: BeloteCompletedTrick[];
  /** Team scores, index = team (seat % 2). */
  teamScores: number[];
  handResults: BeloteHandResult[];
}

export type BeloteAction = { type: 'bid'; suit: Suit } | { type: 'pass' } | { type: 'play'; card: CardId };

export type BeloteLegal =
  | { kind: 'bid'; round: 1 | 2; suits: Suit[] }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface BeloteView {
  gameType: 'BELOTE';
  config: BeloteConfig;
  seat: number | null;
  phase: BelotePhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  upCard: CardId;
  bids: BeloteBid[];
  trump: Suit | null;
  taker: number | null;
  /** True once belote-rebelote has been called, with the calling seat. */
  beloteSeat: number | null;
  tricksWon: number[];
  trick: BeloteTrickPlay[];
  lastTrick: BeloteCompletedTrick | null;
  tricks: BeloteCompletedTrick[];
  teamScores: number[];
  handResults: BeloteHandResult[];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: BeloteConfig = {
  targetScore: 501,
  maxHands: 12,
  handEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const TRICKS = 8;
export const BELOTE_CAPOT_BONUS = 90;
export const BELOTE_BONUS = 20;
export const BELOTE_DIX_DE_DER = 10;
export const BELOTE_HAND_POINTS = 162;

/** Strength order, weakest to strongest, by numeric rank (J=11, Q=12, K=13, A=14). */
const TRUMP_ORDER = [7, 8, 12, 13, 10, 14, 9, 11];
const PLAIN_ORDER = [7, 8, 9, 11, 12, 13, 10, 14];
const TRUMP_POINTS: Record<number, number> = { 11: 20, 9: 14, 14: 11, 10: 10, 13: 4, 12: 3, 8: 0, 7: 0 };
const PLAIN_POINTS: Record<number, number> = { 14: 11, 10: 10, 13: 4, 12: 3, 11: 2, 9: 0, 8: 0, 7: 0 };

export const beloteTeamOf = (seat: number): number => seat % 2;
export const belotePartnerOf = (seat: number): number => (seat + 2) % SEATS;

/** 0 (weakest) .. 7 (strongest) within the card's role. */
export function beloteStrength(card: CardId, trump: Suit): number {
  const c = parseCard(card);
  return (c.suit === trump ? TRUMP_ORDER : PLAIN_ORDER).indexOf(c.rank);
}

export function beloteCardPoints(card: CardId, trump: Suit): number {
  const c = parseCard(card);
  return (c.suit === trump ? TRUMP_POINTS : PLAIN_POINTS)[c.rank] ?? 0;
}

export function beloteTrickWinner(plays: readonly BeloteTrickPlay[], trump: Suit): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    if (c.suit === trump) {
      if (b.suit !== trump || beloteStrength(p.card, trump) > beloteStrength(best.card, trump)) best = p;
    } else if (c.suit === lead && b.suit === lead && beloteStrength(p.card, trump) > beloteStrength(best.card, trump)) {
      best = p;
    }
  }
  return best.seat;
}

/** Cards a seat may play: follow suit, otherwise trump (unless the partner is winning), overtrumping when able. */
export function beloteLegalCards(hand: readonly CardId[], trick: readonly BeloteTrickPlay[], trump: Suit, seat: number): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const trumpsOf = (cards: readonly CardId[]) => cards.filter((c) => parseCard(c).suit === trump);
  const overTrumps = (mine: CardId[]): CardId[] => {
    const table = trumpsOf(trick.map((p) => p.card));
    if (table.length === 0) return mine;
    const top = Math.max(...table.map((c) => beloteStrength(c, trump)));
    const higher = mine.filter((c) => beloteStrength(c, trump) > top);
    return higher.length > 0 ? higher : mine;
  };
  if (lead === trump) {
    const mine = trumpsOf(hand);
    return mine.length > 0 ? overTrumps(mine) : [...hand];
  }
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  if (followers.length > 0) return followers;
  if (beloteTrickWinner(trick, trump) === belotePartnerOf(seat)) return [...hand];
  const mine = trumpsOf(hand);
  return mine.length > 0 ? overTrumps(mine) : [...hand];
}

/**
 * Scores a finished hand. `cardPoints` already holds each team's card points plus dix de der.
 */
export function beloteScoreHand(args: {
  taker: number;
  cardPoints: readonly number[];
  tricksByTeam: readonly number[];
  beloteTeam: number | null;
}): { made: boolean; capotTeam: number | null; points: number[] } {
  const takerTeam = beloteTeamOf(args.taker);
  const defTeam = 1 - takerTeam;
  const capotTeam = args.tricksByTeam[0] === TRICKS ? 0 : args.tricksByTeam[1] === TRICKS ? 1 : null;
  const belote = (team: number) => (args.beloteTeam === team ? BELOTE_BONUS : 0);
  const raw = [0, 1].map((t) => args.cardPoints[t]! + belote(t));
  const made = raw[takerTeam]! > raw[defTeam]!;
  const points = made ? [...raw] : [0, 0];
  if (!made) {
    points[defTeam] = BELOTE_HAND_POINTS + belote(defTeam);
    points[takerTeam] = belote(takerTeam);
  }
  if (capotTeam !== null) points[capotTeam]! += BELOTE_CAPOT_BONUS;
  return { made, capotTeam, points };
}

function newDeck(): CardId[] {
  return buildDeck().filter((c) => parseCard(c).rank >= 7);
}

function deal(state: BeloteState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(newDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let i = 0; i < 20; i++) hands[(state.dealer + 1 + (i % SEATS)) % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.upCard = deck[20]!;
  state.kitty = deck.slice(21);
  state.bids = [];
  state.trump = null;
  state.taker = null;
  state.beloteSeat = null;
  state.beloteAnnounced = false;
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'BID1';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, upCard: state.upCard, counts: state.hands.map((h) => h.length) });
}

/** The taker picks up the turned-up card, everyone is topped up to eight, and play begins. */
function startPlay(state: BeloteState, taker: number, suit: Suit, events: EngineEvent[]): void {
  state.trump = suit;
  state.taker = taker;
  const kitty = [...state.kitty];
  state.hands[taker]!.push(state.upCard);
  for (let i = 0; i < SEATS; i++) {
    const seat = (state.dealer + 1 + i) % SEATS;
    const need = TRICKS - state.hands[seat]!.length;
    state.hands[seat]!.push(...kitty.splice(0, need));
  }
  state.kitty = [];
  state.hands = state.hands.map((h) => sortHand(h));
  const king = `K${suit}`;
  const queen = `Q${suit}`;
  state.beloteSeat = state.hands.findIndex((h) => h.includes(king) && h.includes(queen));
  if (state.beloteSeat < 0) state.beloteSeat = null;
  state.phase = 'PLAYING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'trumpChosen', seat: taker, suit, counts: state.hands.map((h) => h.length) });
}

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: BeloteState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = beloteTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}

function upSuit(state: BeloteState): Suit {
  return parseCard(state.upCard).suit;
}

export const beloteEngine: GameEngine<BeloteConfig, BeloteState, BeloteAction, BeloteView, BeloteLegal> = {
  gameType: 'BELOTE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 2000);
    checkInt('maxHands', c.maxHands, 1, 50);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Belote needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: BeloteState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'BID1',
      hands: [],
      upCard: '7S',
      kitty: [],
      bids: [],
      trump: null,
      taker: null,
      beloteSeat: null,
      beloteAnnounced: false,
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'bid' && typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) return { type: 'bid', suit: raw.suit as Suit };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Belote action');
  },

  applyAction(state, seat, action, rng) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (state.phase === 'BID1' || state.phase === 'BID2') {
      assertEngine(action.type === 'bid' || action.type === 'pass', 'INVALID_PHASE', 'Bidding is in progress');
      const round = state.phase === 'BID1' ? 1 : 2;
      if (action.type === 'bid') {
        const up = upSuit(state);
        assertEngine(round === 1 ? action.suit === up : action.suit !== up, 'ILLEGAL_MOVE', round === 1 ? 'You can only take the turned-up suit' : 'Name a different suit');
        state.bids.push({ seat, round, suit: action.suit });
        events.push({ type: 'bid', seat, round, suit: action.suit });
        startPlay(state, seat, action.suit, events);
        return events;
      }
      state.bids.push({ seat, round, suit: null });
      events.push({ type: 'bid', seat, round, suit: null });
      const passes = state.bids.filter((b) => b.round === round).length;
      if (passes < SEATS) {
        state.turn = (seat + 1) % SEATS;
        return events;
      }
      if (round === 1) {
        state.phase = 'BID2';
        state.turn = (state.dealer + 1) % SEATS;
        return events;
      }
      // Everyone passed twice: throw the hand in and redeal.
      state.dealer = (state.dealer + 1) % SEATS;
      events.push({ type: 'redeal', dealer: state.dealer });
      deal(state, rng, events);
      return events;
    }

    assertEngine(state.phase === 'PLAYING' && action.type === 'play', 'INVALID_PHASE', 'You must play a card');
    const trump = state.trump!;
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(beloteLegalCards(hand, state.trick, trump, seat).includes(action.card), 'ILLEGAL_MOVE', 'You must follow suit, trump or overtrump');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (seat === state.beloteSeat) {
      const other = action.card === `K${trump}` ? `Q${trump}` : action.card === `Q${trump}` ? `K${trump}` : null;
      if (other) {
        const stage = hand.includes(other) ? 'belote' : 'rebelote';
        if (stage === 'rebelote') state.beloteAnnounced = true;
        events.push({ type: stage, seat });
      }
    }
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = beloteTrickWinner(state.trick, trump);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BID1' || state.phase === 'BID2' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BID1') return { kind: 'bid', round: 1, suits: [upSuit(state)] };
    if (state.phase === 'BID2') return { kind: 'bid', round: 2, suits: SUITS.filter((s) => s !== upSuit(state)) };
    if (state.phase !== 'PLAYING') return { kind: 'none' };
    return { kind: 'play', cards: beloteLegalCards(state.hands[seat]!, state.trick, state.trump!, seat) };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BID1' || state.phase === 'BID2') return { type: 'pass' };
    const trump = state.trump!;
    const legal = beloteLegalCards(state.hands[seat]!, state.trick, trump, seat);
    // Cheapest legal card by points, then strength: rarely wastes a winner and is never illegal.
    const lowest = [...legal].sort(
      (a, b) => beloteCardPoints(a, trump) - beloteCardPoints(b, trump) || beloteStrength(a, trump) - beloteStrength(b, trump),
    )[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'BELOTE',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      upCard: state.upCard,
      bids: state.bids.map((b) => ({ ...b })),
      trump: state.trump,
      taker: state.taker,
      beloteSeat: state.beloteAnnounced ? state.beloteSeat : null,
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      handResults: state.handResults.map((r) => ({ ...r, cardPoints: [...r.cardPoints], points: [...r.points] })),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[beloteTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < TRICKS) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const trump = state.trump!;
      const cardPoints = [0, 0];
      const tricksByTeam = [0, 0];
      state.tricks.forEach((t, i) => {
        const team = beloteTeamOf(t.winner);
        tricksByTeam[team]! += 1;
        cardPoints[team]! += t.plays.reduce((n, p) => n + beloteCardPoints(p.card, trump), 0) + (i === TRICKS - 1 ? BELOTE_DIX_DE_DER : 0);
      });
      const beloteTeam = state.beloteSeat === null ? null : beloteTeamOf(state.beloteSeat);
      const scored = beloteScoreHand({ taker: state.taker!, cardPoints, tricksByTeam, beloteTeam });
      scored.points.forEach((p, team) => {
        state.teamScores[team]! += p;
      });
      const result: BeloteHandResult = { taker: state.taker!, trump, cardPoints, beloteTeam, capotTeam: scored.capotTeam, made: scored.made, points: scored.points };
      state.handResults.push(result);
      events.push({ type: 'handEnd', hand: state.hand, result, teamScores: [...state.teamScores], tricksWon: [...state.tricksWon] });
      const cfg = state.config;
      const [a, b] = state.teamScores as [number, number];
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      const capped = state.hand >= cfg.maxHands;
      if ((reached && a !== b) || capped) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'HAND_END';
      }
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
